import React, { useState, useEffect } from 'react';

import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import _setWith from 'lodash/setWith';

import Table from 'cccisd-table';
import HiddenBlock from 'cccisd-hidden-block';

import { switchPlanNameIfNeeded } from '../../../SmartWrapAdminister/helpers';

import style from './style.css';

const CategoryCard = ({ youth, categoryName, isCommentsCategory, categoryHandle, filters, expandAll }) => {
    const [opened, setOpened] = useState(false);

    const isSupervisor = window.cccisd?.fortress?.user?.acting?.role?.handle === 'supervisor';

    useEffect(() => {
        if (expandAll && expandAll.open !== null) {
            setOpened(expandAll.open);
        } else if (expandAll && expandAll.open === null) {
            setOpened(false);
        }
    }, [expandAll?.cnt]);

    const allAnswerData = useSelector(state => {
        return state.app.smartwrap.dashboard.allAnswerData;
    });

    const toggleOpen = () => setOpened(prev => !prev);

    const youthCaregivers = [...youth.parentRoles.caregiverList.map(caregiver => caregiver.pawn.pawnId)];
    const youthPawnId = youth.pawn.pawnId;
    const youthCareCoordinator = [youth.parentRoles.careCoordinatorList?.map(cc => cc.user.fullName)];

    const youthQuestionColumns = [
        {
            name: 'question',
            label: 'Question',
            filter: true,
        },
        {
            name: 'answer',
            label: 'Response Text',
            filter: true,
        },
        {
            name: 'answerValue',
            label: 'Response Value',
            filter: true,
        },
        {
            name: 'responseDate',
            label: 'Response Date',
            sort: true,
            filter: true,
            render: responseDate => {
                return responseDate ? responseDate.split(' ')[0] : '';
            },
        },
        {
            name: 'planName',
            label: 'Plan Name',
            sort: true,
            filter: true,
            render: planName => {
                return switchPlanNameIfNeeded(planName);
            },
        },
        {
            name: 'planWeek',
            label: 'Plan Week',
            sort: true,
            filter: true,
        },
    ];

    const youthCommentColumns = [
        {
            name: 'answer',
            label: 'Comment',
            sort: true,
        },
        {
            name: 'responseDate',
            label: 'Response Date',
            filter: true,
            render: responseDate => {
                return responseDate ? responseDate.split(' ')[0] : '';
            },
        },
        {
            name: 'planName',
            label: 'Plan Name',
            filter: true,
            render: planName => {
                return switchPlanNameIfNeeded(planName);
            },
        },
        {
            name: 'planWeek',
            label: 'Plan Week',
            filter: true,
        },
    ];

    const caregiverQuestionColumns = [
        {
            name: 'question',
            label: 'Question',
            filter: true,
        },
        {
            name: 'answer',
            label: 'Response Text',
            filter: true,
        },
        {
            name: 'answerValue',
            label: 'Response Value',
            filter: true,
        },
        {
            name: 'responseDate',
            label: 'Response Date',
            sort: true,
            filter: true,
            render: responseDate => {
                return responseDate ? responseDate.split(' ')[0] : '';
            },
        },
        {
            name: 'planName',
            label: 'Plan Name',
            sort: true,
            filter: true,
            render: planName => {
                return switchPlanNameIfNeeded(planName);
            },
        },
        {
            name: 'planWeek',
            label: 'Plan Week',
            sort: true,
            filter: true,
        },
    ];

    const caregiverCommentColumns = [
        {
            name: 'answer',
            label: 'Comment',
            sort: true,
        },
        {
            name: 'responseDate',
            label: 'Response Date',
            filter: true,
            render: responseDate => {
                return responseDate ? responseDate.split(' ')[0] : '';
            },
        },
        {
            name: 'planName',
            label: 'Plan Name',
            filter: true,
            render: planName => {
                return switchPlanNameIfNeeded(planName);
            },
        },
        {
            name: 'planWeek',
            label: 'Plan Week',
            filter: true,
        },
    ];

    // match score format in aggregate data tab bar graph
    const formatAverage = value => {
        const two = value.toFixed(2);
        return two.endsWith('0') ? value.toFixed(1) : two;
    };

    const TableSection = ({ columns, data }) => {
        let youthAverage = 0;
        let sum = 0;
        let numberOfQuestionsAnswered = 0;
        Object.keys(_get(data, 'scoreAverages', [])).forEach(planName => {
            Object.keys(data.scoreAverages[planName]).forEach(timepoint => {
                let averagesData = _get(data, `scoreAverages.${planName}.${timepoint}.${youthPawnId}`, false);

                if (averagesData) {
                    sum += averagesData.sum;
                    numberOfQuestionsAnswered += averagesData.numberOfQuestionsAnswered;
                }
            });
        });

        youthAverage =
            isCommentsCategory || numberOfQuestionsAnswered === 0 ? '' : formatAverage(sum / numberOfQuestionsAnswered);

        return (
            <div>
                <div className={style.table_section}>
                    <div className={style.table_section_header}>
                        <div className={style.label_bold_18px}>Youth ({youth.fields.wrapId})</div>
                        {youthAverage && (
                            <div className={style.label_18px}>
                                Youth Average:{` `}
                                <b>{youthAverage}</b>
                                {` / 3`}
                            </div>
                        )}
                    </div>
                    <div className={style.table_wrapper}>
                        <Table
                            rowKey="variableKey"
                            perPage={5}
                            perPageOptions={[5, 10, 15, 20, 25, 50, 100]}
                            showBottomPaginator
                            columns={columns[0]}
                            data={data.youth}
                            showRowActions={false}
                            hideShowingRowsSummary
                        />
                    </div>
                </div>

                <div className={style.dividing_line} />
                {youth.parentRoles.caregiverList.map((caregiver, i) => {
                    let caregiverAverage = 0;
                    let caregiverSum = 0;
                    let caregiverNumberOfQuestionsAnswered = 0;
                    const caregiverPawnId = caregiver.pawn.pawnId;

                    const caregiverHasCommentsAndYouthDoesNot =
                        isCommentsCategory &&
                        _get(data, 'youth', []).length === 0 &&
                        _get(data, `caregivers[${caregiverPawnId}]`, []).length > 0;

                    Object.keys(_get(data, 'scoreAverages', [])).forEach(planName => {
                        Object.keys(data.scoreAverages[planName]).forEach(timepoint => {
                            let averagesData = _get(
                                data,
                                `scoreAverages.${planName}.${timepoint}.${caregiverPawnId}`,
                                false
                            );

                            if (averagesData) {
                                caregiverSum += averagesData.sum;
                                caregiverNumberOfQuestionsAnswered += averagesData.numberOfQuestionsAnswered;
                            }
                        });
                    });

                    caregiverAverage =
                        isCommentsCategory || caregiverNumberOfQuestionsAnswered === 0
                            ? ''
                            : formatAverage(caregiverSum / caregiverNumberOfQuestionsAnswered);

                    return (
                        <div key={i}>
                            <HiddenBlock
                                key={expandAll ? `${expandAll.cnt}-${i}` : i}
                                title={
                                    <>
                                        {caregiverAverage && (
                                            <span
                                                className={style.label_18px}
                                                style={{ float: 'right', color: '#212529' }}
                                            >
                                                Caregiver Average:{` `}
                                                <b>{caregiverAverage}</b>
                                                {` / 3`}
                                            </span>
                                        )}
                                        <span className={style.label_bold_18px}>{caregiver.fields.caregiverName}</span>
                                    </>
                                }
                                show={(() => {
                                    if (expandAll && expandAll.open !== null) {
                                        return expandAll.open;
                                    }
                                    return (!youthAverage && caregiverAverage) || caregiverHasCommentsAndYouthDoesNot;
                                })()}
                            >
                                <div>
                                    <div className={style.table_section}>
                                        <div className={style.table_wrapper}>
                                            <Table
                                                rowKey="variableKey"
                                                perPage={5}
                                                perPageOptions={[5, 10, 15, 20, 25, 50, 100]}
                                                showBottomPaginator
                                                columns={columns[1]}
                                                data={data.caregivers[caregiverPawnId] || []}
                                                showRowActions={false}
                                                hideShowingRowsSummary
                                            />
                                        </div>
                                    </div>
                                </div>
                            </HiddenBlock>
                        </div>
                    );
                })}
            </div>
        );
    };

    const getTableDataForYouth = () => {
        let res = { youth: [], caregivers: {} };

        const startDateFilter = filters.startDate ? filters.startDate : false;
        const endDateFilter = filters.endDate ? filters.endDate : false;
        const planNameFilter = filters.planName;

        const categoryData = _get(allAnswerData, categoryHandle, {});

        let scoreAverages = {}; // by pawnId
        Object.keys(categoryData).forEach(variableKey => {
            const variableData = categoryData[variableKey];

            if (variableData.answers) {
                let youthAnswer = _get(variableData.answers, youthPawnId, false);
                if (youthAnswer) {
                    // check against filters
                    const responseDate = new Date(youthAnswer.responseDate);
                    if (
                        (!startDateFilter || startDateFilter <= responseDate) &&
                        (!endDateFilter || endDateFilter >= responseDate) &&
                        (!planNameFilter || planNameFilter.toLowerCase() === youthAnswer.planName.toLowerCase()) &&
                        (!isCommentsCategory || (youthAnswer.answer && youthAnswer.answer.trim() !== ''))
                    ) {
                        // collect answers from youth
                        youthAnswer = { ...youthAnswer };
                        res.youth.push(youthAnswer);

                        // average calculation for youth here
                        const pawnId = youthAnswer.pawnId;
                        const planName = youthAnswer.planName;
                        const timepoint = youthAnswer.timepoint;
                        const answerValue = youthAnswer.answerValue;

                        let sum = _get(scoreAverages, `${planName}.${timepoint}.${pawnId}.sum`, 0);
                        let numberOfQuestionsAnswered = _get(
                            scoreAverages,
                            `${planName}.${timepoint}.${pawnId}.numberOfQuestionsAnswered`,
                            0
                        );

                        _setWith(scoreAverages, `${planName}.${timepoint}.${pawnId}.sum`, sum + answerValue, Object);
                        _setWith(
                            scoreAverages,
                            `${planName}.${timepoint}.${pawnId}.numberOfQuestionsAnswered`,
                            numberOfQuestionsAnswered + 1,
                            Object
                        );
                    }
                }

                // collect answers from the caregivers
                youthCaregivers.forEach(caregiverPawnId => {
                    let caregiverAnswer = _get(variableData.answers, `${caregiverPawnId}_${youthPawnId}`, false);

                    if (caregiverAnswer) {
                        // check against filters
                        const responseDate = new Date(caregiverAnswer.responseDate);
                        if (
                            (!startDateFilter || startDateFilter <= responseDate) &&
                            (!endDateFilter || endDateFilter >= responseDate) &&
                            (!planNameFilter ||
                                planNameFilter.toLowerCase() === caregiverAnswer.planName.toLowerCase()) &&
                            (!isCommentsCategory || (caregiverAnswer.answer && caregiverAnswer.answer.trim() !== ''))
                        ) {
                            caregiverAnswer = { ...caregiverAnswer };
                            if (!res.caregivers[caregiverPawnId]) {
                                res.caregivers[caregiverPawnId] = [];
                            }
                            res.caregivers[caregiverPawnId].push(caregiverAnswer);

                            // average calculation for caregiver here
                            const pawnId = caregiverPawnId;
                            const planName = caregiverAnswer.planName;
                            const timepoint = caregiverAnswer.timepoint;
                            const answerValue = caregiverAnswer.answerValue;

                            let sum = _get(scoreAverages, `${planName}.${timepoint}.${pawnId}.sum`, 0);
                            let numberOfQuestionsAnswered = _get(
                                scoreAverages,
                                `${planName}.${timepoint}.${pawnId}.numberOfQuestionsAnswered`,
                                0
                            );

                            _setWith(
                                scoreAverages,
                                `${planName}.${timepoint}.${pawnId}.sum`,
                                sum + answerValue,
                                Object
                            );
                            _setWith(
                                scoreAverages,
                                `${planName}.${timepoint}.${pawnId}.numberOfQuestionsAnswered`,
                                numberOfQuestionsAnswered + 1,
                                Object
                            );
                        }
                    }
                });
            }
        });

        res.scoreAverages = scoreAverages;

        return res;
    };

    const data = getTableDataForYouth();

    return (
        <div className={style.category_card_container} onClick={toggleOpen}>
            <div className={style.category_card_front}>
                <div
                    className={style.category_card_arrow}
                    style={{
                        transform: opened ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                >
                    ▶
                </div>
                <div className={style.label_bold_20px} style={{ marginLeft: '12px' }}>
                    {categoryName}
                </div>
                {isSupervisor && (
                    <div style={{ marginLeft: 'auto', fontSize: '14px' }}>
                        Care Coordinator:
                        <div>{youthCareCoordinator}</div>
                    </div>
                )}
            </div>

            {opened && (
                <div className={style.category_card_body_container} onClick={e => e.stopPropagation()}>
                    <TableSection
                        columns={
                            isCommentsCategory
                                ? [youthCommentColumns, caregiverCommentColumns]
                                : [youthQuestionColumns, caregiverQuestionColumns]
                        }
                        data={data}
                    />
                </div>
            )}
        </div>
    );
};

export default CategoryCard;
